import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Tabs,
  Tab,
  CircularProgress,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { useAuth } from '../../context/AuthContext';
import { getPedidos } from '../../services/pedidos';
import { getDirecciones } from '../../services/direcciones';

// Componentes de las diferentes secciones
import CustomerProfile from './CustomerProfile';
import CustomerOrders from './CustomerOrders';
import CustomerAddresses from './CustomerAddresses';
import CustomerPayments from './CustomerPayments';

const DashboardContainer = styled(Box)({
  minHeight: '100vh',
});

const StatsCard = styled(Card)({
  borderRadius: '20px',
  boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
  border: '1px solid rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 40px rgba(0,0,0,0.12)',
  },
});

const TabPanel = (props: { children?: React.ReactNode; value: number; index: number }) => {
  const { children, value, index } = props;
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const CustomerDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    totalSpent: 0,
    addressCount: 0,
  });
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  // Redirigir a login si no está autenticado
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  // Detectar ruta para abrir pestaña específica
  useEffect(() => {
    const path = location.pathname;
    
    const routeToTabMap: { [key: string]: number } = {
      '/mi-cuenta': 0,
      '/mi-cuenta/perfil': 0,
      '/mi-cuenta/pedidos': 1,
      '/mi-cuenta/direcciones': 2,
      '/mi-cuenta/metodos-pago': 3,
    };
    
    const tabIndex = routeToTabMap[path];
    if (tabIndex !== undefined) {
      setTabValue(tabIndex);
    }
    
    // También soportar el parámetro ?tab= para compatibilidad
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    
    if (tabParam) {
      const paramValue = parseInt(tabParam);
      if (!isNaN(paramValue) && paramValue >= 0 && paramValue <= 3) {
        setTabValue(paramValue);
      }
    }
  }, [location]);

  useEffect(() => {
    const fetchUserStats = async () => {
      if (!user) {
        setStatsLoading(false);
        return;
      }

      try {
        // Obtener todos los pedidos del usuario desde Django
        const pedidosData = await getPedidos();
        const pedidos = Array.isArray(pedidosData) ? pedidosData : [];
        
        let total = pedidos.length;
        let pending = pedidos.filter(p => p.estado === 'pendiente' || p.estado === 'confirmado').length;
        let totalSpent = 0;

        // Calcular total gastado (solo pedidos entregados y pagados)
        pedidos.forEach((pedido) => {
          if (pedido.estado === 'entregado' && pedido.pagado) {
            totalSpent += parseFloat(pedido.total) || 0;
          }
        });

        // Obtener direcciones del usuario desde Django
        const direccionesData = await getDirecciones();
        const direcciones = Array.isArray(direccionesData) ? direccionesData : [];
        const addressCount = direcciones.length;

        setStats({
          totalOrders: total,
          pendingOrders: pending,
          totalSpent: totalSpent,
          addressCount: addressCount,
        });
      } catch (error) {
        console.error('Error fetching user stats:', error);
        setStats({
          totalOrders: 0,
          pendingOrders: 0,
          totalSpent: 0,
          addressCount: 0,
        });
      } finally {
        setStatsLoading(false);
      }
    };

    fetchUserStats();
  }, [user]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Navegar a la ruta correspondiente
    const tabToRouteMap: { [key: number]: string } = {
      0: '/mi-cuenta/perfil',
      1: '/mi-cuenta/pedidos',
      2: '/mi-cuenta/direcciones',
      3: '/mi-cuenta/metodos-pago',
    };
    
    const route = tabToRouteMap[newValue];
    if (route) {
      navigate(route);
    }
  };

  // Mostrar loader mientras se verifica la autenticación o se cargan las estadísticas
  if (authLoading || statsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#2e5a33' }} />
      </Box>
    );
  }

  // Si no está autenticado, no renderizar nada (el useEffect redirigirá)
  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <DashboardContainer>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Título de la página */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" sx={{ 
            fontWeight: 'bold', 
            color: '#2e5a33',
            mb: 1,
            fontFamily: "'Playfair Display', serif"
          }}>
            Mi Cuenta
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Gestiona tu perfil y pedidos
          </Typography>
        </Box>
        {/* Stats Cards */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: '1fr 1fr', 
            md: '1fr 1fr 1fr 1fr' 
          }, 
          gap: 3, 
          mb: 4 
        }}>
          <StatsCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Avatar sx={{ mx: 'auto', mb: 2, bgcolor: '#2e5a33' }}>
                <ShoppingBagIcon />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e5a33', mb: 1 }}>
                {stats.totalOrders}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Pedidos Realizados
              </Typography>
            </CardContent>
          </StatsCard>
          
          <StatsCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Avatar sx={{ mx: 'auto', mb: 2, bgcolor: '#ff9800' }}>
                <TrackChangesIcon />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 1 }}>
                {stats.pendingOrders}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Pedidos Pendientes
              </Typography>
            </CardContent>
          </StatsCard>

          <StatsCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Avatar sx={{ mx: 'auto', mb: 2, bgcolor: '#4caf50' }}>
                <PaymentIcon />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50', mb: 1 }}>
                ${stats.totalSpent.toLocaleString('es-CL')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Gastado
              </Typography>
            </CardContent>
          </StatsCard>

          <StatsCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Avatar sx={{ mx: 'auto', mb: 2, bgcolor: '#f44336' }}>
                <LocationOnIcon />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f44336', mb: 1 }}>
                {stats.addressCount}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Direcciones Guardadas
              </Typography>
            </CardContent>
          </StatsCard>
        </Box>

        {/* Navigation Tabs */}
        <Card sx={{ borderRadius: '20px', mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                fontWeight: 'bold',
                fontSize: { xs: '14px', sm: '16px' },
                minWidth: { xs: '100px', sm: '120px' },
                px: { xs: 1, sm: 2 },
                '&.Mui-selected': {
                  color: '#2e5a33',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#2e5a33',
                height: '3px',
              },
              '& .MuiTabs-scrollButtons': {
                color: '#2e5a33',
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
            }}
          >
            <Tab label="Mi Perfil" />
            <Tab label="Mis Pedidos" />
            <Tab label="Direcciones" />
            <Tab label="Métodos de Pago" />
          </Tabs>
        </Card>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <CustomerProfile />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <CustomerOrders />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <CustomerAddresses />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <CustomerPayments />
        </TabPanel>
      </Container>
    </DashboardContainer>
  );
};

export default CustomerDashboard;
